


import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../PageRouting/AppRoutes";
import progressIcon from "../../Assets/Images/AiReport/progressIcon.svg";
import successIcon from "../../Assets/Images/rewamp/upload complete.svg";
import visionProgressIcon from "../../Assets/Images/rewamp/vision care internal screens/upload eye image-progress.svg";
import visionSuccessIcon from "../../Assets/Images/rewamp/vision care internal screens/upload complete.svg";

// Services with their own banner icons (others use the shared progress / success icons)
const BANNER_ICONS = {
  vision_care: { progress: visionProgressIcon, success: visionSuccessIcon },
};

const reportContent = {
  skin_care: {
    title: "Uploading your SkinCare image…",
    titleSuccess: "Your image upload is complete!",
    subtitle: "It will take 60 seconds. You can continue browsing while we analyze your data.",
    subtitleSuccess: "Click 'View Report' to open it in your AI Reports section.",
  },
  oral_care: {
    title: "Uploading your OralCare image…",
    titleSuccess: "Your image upload is complete!",
    subtitle: "It will take 60 seconds. You can continue browsing while we analyze your data.",
    subtitleSuccess: "Click 'View Report' to open it in your AI Reports section.",
  },
  lungs_care: {
    title: "Uploading your LungsCare audio…",
    titleSuccess: "Your audio upload is complete!",
    subtitle: "It will take 60 seconds. You can continue browsing while we analyze your data.",
    subtitleSuccess: "Click 'View Report' to open it in your AI Reports section.",
  },
  smart_vitals: {
    title: "Uploading your facial scan video...",
    titleSuccess: "Your video is successfully uploaded!",
    subtitle: "This will take a few seconds. You can continue browsing while we process your data.",
    subtitleSuccess: "Click 'View Report' to open it in your AI Reports section.",
  },
  vision_care: {
    title: "Uploading your Eye image...",
    titleSuccess: "Your Eye image upload is complete!",
    subtitle: "This will take a few seconds. You can continue browsing while we process your image.",
    subtitleSuccess: "Click 'View Report' to view your Visioncare report in your AI Reports section.",
  },
};

export default function AIReportProgressBanner({
  visible = true,
  onCancel,
  onView,
  percentOverride,
}) {
  const [secLeft, setSecLeft] = useState(60);
  const quantumUpload = JSON.parse(localStorage.getItem("quantumUploadSession")) || {};
  const serviceLabel = quantumUpload?.serviceName;
  const keyMap = {
    "Skin Care": "skin_care",
    "Lung Care": "lungs_care",
    "Smart Vitals": "smart_vitals",
    "Oral Care": "oral_care",
    "Vision Care": "vision_care",
  };
  const solutionType = keyMap[serviceLabel] || "skin_care";

  useEffect(() => {
    if (!visible) return;
    if (typeof percentOverride === "number") return; // ✅ skip fake timer when real % given
    setSecLeft(60);
    const t = setInterval(() => {
      setSecLeft((s) => (s > 0 ? s - 1 : 0)); // ✅ s-1 not s-10
    }, 1000);
    return () => clearInterval(t);
  }, [visible, percentOverride]);

  const percent = typeof percentOverride === "number"
    ? Math.min(100, Math.max(0, Math.round(percentOverride)))
    : Math.min(100, Math.round(((60 - secLeft) / 60) * 100));

  const navigate = useNavigate();
  if (!visible) return null;

  return (
    <div className="w-full flex justify-center">
      <div
        className="w-full md:w-[960px] flex flex-col justify-center pt-3 rounded-[4px] mx-2 md:mx-0 relative"
        style={{
          background: "var(--section-bg-gradient), #fff"
        }}
      >
        <button
          onClick={() => onCancel?.()}
          className="text-[#004276] font-inter font-semibold text-[16px]"
          style={{ position: "absolute", top: 8, right: 8 }}
        >
          ×
        </button>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full p-3 gap-2 md:gap-0">
          <div className="flex items-center gap-3">
            <img
              src={percent === 100
                ? (BANNER_ICONS[solutionType]?.success || successIcon)
                : (BANNER_ICONS[solutionType]?.progress || progressIcon)}
              alt="status icon"
              className="w-[40px] h-[40px] md:w-[50px] md:h-[50px] object-cover"
            />
            <div className="flex flex-col">
              <p
                className="font-inter font-bold text-sm md:text-[16px] leading-[20px] md:leading-[24px] mb-0"
                style={{
                  background: "var(--primary-gradient)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent"
                }}
              >
                {percent === 100
                  ? reportContent[solutionType]?.titleSuccess
                  : reportContent[solutionType]?.title}
              </p>
              <p
                className="font-inter font-semibold text-xs md:text-[16px] mb-0 leading-[1.4] pb-[2px]"
                style={{
                  background: "var(--primary-gradient)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  display: "inline-block"
                }}
              >
                {percent === 100
                  ? reportContent[solutionType]?.subtitleSuccess
                  : reportContent[solutionType]?.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto justify-end">
            {percent === 100 ? (
              <>
                <button
                  className="rounded-[6px] px-[16px] py-[4px] md:px-[24px] md:py-[6px] font-inter font-semibold text-xs md:text-[16px] mt-2 text-white hover:opacity-90 active:opacity-80"
                  style={{ background: "var(--color-brand-primary)" }}
                  onClick={() => {
                    onView
                      ? onView(solutionType)
                      : navigate(APP_ROUTES.AI_REPORT, { state: { tab: solutionType } });
                  }}
                >
                  View Report
                </button>
                <div
                  onClick={() => onCancel?.()}
                  style={{
                    textDecoration: "underline",
                    textDecorationStyle: "solid",
                    textUnderlineOffset: 0,
                  }}
                  className="mt-2 cursor-pointer text-[#666666] hover:opacity-90 active:opacity-80 font-inter font-semibold text-xs md:text-[16px]"
                >
                  Dismiss
                </div>
              </>
            ) : (
              typeof percentOverride === "number"
                ? <p className="font-inter font-bold text-sm md:text-[16px] text-[#FF8D28]">{percent}%</p>
                : <p className="font-inter font-bold text-sm md:text-[16px] text-[#FF8D28]">{secLeft} sec left</p>
            )}
          </div>
        </div>
        <div className="w-full h-[8px] md:h-[12px] bg-[#D2D2D2] rounded-full overflow-hidden mt-2 md:mt-0">
          <div
            className="h-full"
            style={{
              width: `${percent}%`,
              backgroundColor: percent === 100 ? "#198038" : "#FF8D28",
              transition: "width 0.6s ease, background-color 0.3s ease",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
}